import {
    Component,
    Element,
    h,
    Host,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { PortalServiceTileEvents } from '../../models/appInsights/events/portal-service-tile.events';
import store from '../../store/store';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import Logger from '../../utils/logger/logger';
import { trackStatsigEvent } from '../../utils/logger/statsig';
import getSvg from '../../utils/serviceIconHelper/serviceIconHelper';
import { getAccessToken } from '../../utils/session/sessionStore';

@Component({
    tag: 'portal-service-tile',
    styleUrl: 'portal-service-tile.scss',
    shadow: true,
})
export class PortalServiceTile {
    @Element() el: HTMLPortalServiceTileElement;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() active: boolean;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() handled: boolean = false;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() serviceid: string;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() url: string;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() name: string;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() showName: boolean = true;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() svg?: string;

    /**
     * Name of the icon
     * @memberof PortalServiceTile
     */
    @Prop() listMode?: boolean;

    /**
    * Theme override to bypass store state
    * @memberof PortalServiceTile
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Shows a red badge
     */
    @Prop() showBadge = false;

    /**
     * Treat space as click instead of changing the url (for more button)
     */
    @Prop() handleSpaceAsClick = false;

    /**
     * An optional onClick callback that runs when the button is clicked
     */
    @Prop() serviceClickHandler?: (event: any) => void;

    /**
     * An optional callback that runs when menu is closed
     */
    @Prop() closeCallback?: () => void;

    /**
     * Checks if the button is the last item in the menu
     */
    @Prop() isLast?: boolean = false;

    /**
     * (Internal Only) Force enable the new left nav design
     * @memberof ApNav
     */
    @Prop() forceEnableLeftNavRedesign: boolean = false;

    render() {
        const enableLeftNavRedesign = getFeatureFlag(Features.EnableLeftNavRedesign.name) || this.forceEnableLeftNavRedesign;

        return <Host class={{ redesign: enableLeftNavRedesign }}>
            <portal-tooltip
                text={this.name}
                disabled={!enableLeftNavRedesign || this.showName}
                theme={this.theme}
            >
                <a
                    href={this.url}
                    aria-labelledby={`menu-text-${this.serviceid}`}
                    id={`nav-tile-${this.serviceid}`}
                    data-cy={this.listMode ? 'nav-waffle-menu-' + this.serviceid : 'nav-tile-' + this.serviceid}
                    onKeyDown={(e) => this.handleKeyDown(e)}
                    class={`${this.listMode ? 'pt-menu-waffle-item' : 'pt-menu-root'} ${this.active ? 'active' : 'inactive'}`}
                    onClick={this.onClickHandler}
                    onBlur={(this.closeCallback && this.isLast && this.listMode) ? this.closeCallback : undefined}
                    title={!enableLeftNavRedesign && !this.listMode ? this.name : undefined}
                    {...(this.serviceid === 'more' ? { ariaExpanded: this.active } : {})}>
                    <div data-test={'nav-tile-' + this.serviceid} class={`${this.listMode ? 'pt-menu-waffle-face' : 'pt-menu-face'}`}>
                        <div
                            data-test={`nav-tile-svg-${this.serviceid}`}
                            class={`${this.listMode ? 'pt-waffle-icon' : ''} ${this.active ? 'pt-icon-active' : 'pt-icon'}`}
                            innerHTML={getSvg(this.svg ?? '')}
                        />
                        {this.name && this.showName && (
                            <div
                                class={`${this.listMode ? 'pt-menu-waffle-text' : 'pt-menu-text'}`}
                                id={`menu-text-${this.serviceid}`}
                                data-test={`menu-text-${this.serviceid}`}
                                style={this.listMode ? {} : this.tileStyle()}>
                                {this.name}
                            </div>
                        )}
                        {this.showBadge && (
                            <div
                                data-test={`nav-tile-badge-${this.serviceid}`}
                                class={this.listMode ? 'pt-list-badge' : 'pt-badge'}
                            />
                        )}
                    </div>
                </a>
            </portal-tooltip>
        </Host>;
    }

    private handleKeyDown(e: KeyboardEvent): void {
        if (e.key === ' ') {
            if (this.handleSpaceAsClick) {
                this.onClickHandler(e);
                return;
            }

            e.preventDefault();
            window.location.href = this.url;
        }
    }

    private onClickHandler = (_e) => {
        Logger.logCustomEvent(PortalServiceTileEvents.ClickedService, {
            service: this.serviceid,
            location: this.listMode ? 'waffle' : 'nav',
        });
        trackStatsigEvent(getAccessToken(), PortalServiceTileEvents.ClickedService, this.serviceid,
            { location: this.listMode ? 'waffle' : 'nav' });

        if (this.handled) {
            _e.preventDefault();
            store.get('settingClickEmitter')?.emit({
                serviceid: this.serviceid,
                url: this.url,
            });
            return;
        }

        if (this.serviceClickHandler) {
            this.serviceClickHandler(_e);
            return;
        }
    };

    private tileStyle = () => {
        const maxLength = Math.max(...this.name.split(' ').map((word) => word.length));

        if (maxLength < 10) {
            return { 'letter-spacing': '' };
        }

        return {
            'letter-spacing': `${(10 - maxLength) * 0.3 + 0.1}px`,
            'max-width': 'unset',
        };
    };
}
